import { Link, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Building2, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';

export const PublicLayout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isHome = location.pathname === '/';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-colors duration-300',
          isHome ? 'bg-transparent' : 'bg-white border-b border-border'
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div
                className={cn(
                  'w-10 h-10 rounded-xl flex items-center justify-center',
                  isHome ? 'bg-white/20 backdrop-blur-sm' : 'bg-primary'
                )}
              >
                <Building2
                  className={cn('h-6 w-6', isHome ? 'text-white' : 'text-primary-foreground')}
                />
              </div>
              <span
                className={cn(
                  'font-serif text-xl font-semibold',
                  isHome ? 'text-white' : 'text-foreground'
                )}
              >
                Resident Hotels
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className={cn(
                  'text-sm font-medium transition-colors',
                  isHome
                    ? 'text-white/80 hover:text-white'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Home
              </Link>
              <Link
                to="/properties"
                className={cn(
                  'text-sm font-medium transition-colors',
                  isHome
                    ? 'text-white/80 hover:text-white'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Properties
              </Link>
              <Link
                to="/booking/lookup"
                className={cn(
                  'text-sm font-medium transition-colors',
                  isHome
                    ? 'text-white/80 hover:text-white'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                My Booking
              </Link>
              <Link to="/admin/login">
                <Button
                  variant={isHome ? 'secondary' : 'outline'}
                  size="sm"
                  className={cn(
                    'rounded-full px-6',
                    isHome && 'bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20'
                  )}
                  data-testid="admin-login-btn"
                >
                  Admin
                </Button>
              </Link>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              data-testid="mobile-nav-toggle"
            >
              {mobileMenuOpen ? (
                <X className={cn('h-6 w-6', isHome ? 'text-white' : 'text-foreground')} />
              ) : (
                <Menu className={cn('h-6 w-6', isHome ? 'text-white' : 'text-foreground')} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-border">
            <nav className="px-4 py-4 space-y-2">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-foreground hover:bg-muted rounded-lg"
              >
                Home
              </Link>
              <Link
                to="/properties"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-foreground hover:bg-muted rounded-lg"
              >
                Properties
              </Link>
              <Link
                to="/booking/lookup"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-foreground hover:bg-muted rounded-lg"
              >
                My Booking
              </Link>
              <Link
                to="/admin/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-primary font-medium hover:bg-muted rounded-lg"
              >
                Admin Login
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className={cn('flex-1', !isHome && 'pt-20')}>{children}</main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <span className="font-serif text-xl font-semibold">Resident Hotels</span>
              </div>
              <p className="text-white/70 leading-relaxed max-w-md">
                Experience luxury hospitality at its finest. Our carefully curated collection of
                properties offers unparalleled comfort and service.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-white/70 hover:text-white transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/properties" className="text-white/70 hover:text-white transition-colors">
                    Properties
                  </Link>
                </li>
                <li>
                  <Link
                    to="/booking/lookup"
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    Find Booking
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-white/70">
                <li>support@residenthotels.com</li>
                <li>+1 (555) 123-4567</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/50 text-sm">
            © {new Date().getFullYear()} Resident Hotels. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};
